/**
 * iter213c — Technician "Required Work" chooser.
 *
 * Route: /app/tech/job/:id/required-work
 *
 * Shown when the /gh/open technician deep-link resolves to a job that
 * still has ≥2 outstanding execution workflows. Reads the canonical
 * outstanding-workflow list from
 *   GET /api/tech/job/:id/required-work
 * so the client and the deep-link resolver stay in lock-step. If
 * nothing is outstanding by the time the tech arrives (e.g. FOH
 * refresh raced them), the page pushes them straight to the GH case
 * page so they don't stare at an empty "Required Work" screen.
 *
 * UX rules from the spec:
 *   - Large tap targets, minimal text, no FOH controls.
 *   - Only outstanding workflows offered as Start actions.
 *   - Completed workflows appear below as a subtle audit.
 *   - No duplicate "Service" + "VI" — canonicalised server-side.
 */
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import {
  ClipboardList, Stethoscope, Search as SearchIcon, Loader2,
  AlertCircle, CheckCircle2, ArrowRight,
} from "lucide-react";
import Header from "@/components/shared/Header";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ICON_MAP = {
  mot:                 ClipboardList,
  diagnostic:          Stethoscope,
  vehicle_inspection:  SearchIcon,
};

const WORKFLOW_TINT = {
  mot: {
    ring:  "border-teal-400",
    tile:  "bg-teal-50 hover:bg-teal-100",
    text:  "text-teal-800",
    icon:  "text-teal-700",
  },
  diagnostic: {
    ring:  "border-amber-400",
    tile:  "bg-amber-50 hover:bg-amber-100",
    text:  "text-amber-900",
    icon:  "text-amber-700",
  },
  vehicle_inspection: {
    ring:  "border-blue-400",
    tile:  "bg-blue-50 hover:bg-blue-100",
    text:  "text-blue-900",
    icon:  "text-blue-700",
  },
};

export default function RequiredWorkPage() {
  const { id: jobId } = useParams();
  const navigate = useNavigate();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API}/tech/job/${jobId}/required-work`);
        if (cancelled) return;
        // If nothing is outstanding by the time we render (FOH-side
        // completion raced the tech's click), bounce to the shared GH
        // case page rather than showing an empty chooser.
        const remaining = r.data?.outstanding_count || 0;
        if (remaining === 0) {
          if (r.data?.intake_session_id) {
            navigate(`/app/foh/intake/gh-case/${r.data.intake_session_id}`, { replace: true });
          } else {
            navigate("/app/tech/jobs", { replace: true });
          }
          return;
        }
        // iter213d — single outstanding workflow: auto-forward to it
        // instead of forcing the tech through a one-item chooser. The
        // /gh/open resolver already skips /required-work in this
        // case, but manually-typed or bookmarked URLs land here too,
        // and the chooser is redundant for a single choice.
        if (remaining === 1) {
          const only = (r.data?.workflows || []).find((w) => w.outstanding);
          if (only?.start_url) {
            navigate(only.start_url, { replace: true });
            return;
          }
        }
        setData(r.data);
      } catch (e) {
        if (!cancelled) {
          setError(
            e?.response?.data?.detail ||
            e?.message ||
            "Couldn't load Required Work for this job.",
          );
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [jobId, navigate]);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-100">
        <Header />
        <div className="max-w-lg mx-auto px-4 py-10 flex items-center justify-center text-slate-500 gap-2"
             data-testid="required-work-loading">
          <Loader2 className="w-5 h-5 animate-spin" />
          <span>Loading required work…</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-slate-100">
        <Header />
        <div className="max-w-lg mx-auto px-4 py-10" data-testid="required-work-error">
          <div className="bg-white border border-red-200 rounded-xl p-5 shadow-sm">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-red-600 shrink-0 mt-0.5" />
              <div>
                <h1 className="text-base font-bold text-slate-900">
                  Couldn&apos;t load required work
                </h1>
                <p className="mt-1 text-sm text-slate-600">{error}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const workflows = data?.workflows || [];
  const outstanding = workflows.filter((w) => w.outstanding);
  const completed   = workflows.filter((w) => !w.outstanding);

  const vehicleLine = [data?.make, data?.model].filter(Boolean).join(" ");
  const customerLine = data?.customer_name || "";

  return (
    <div className="min-h-screen bg-slate-100 pb-24">
      <Header />
      <div className="max-w-lg mx-auto px-4 py-5" data-testid="required-work-page">
        {/* Vehicle / customer / GH reference banner */}
        <section
          className="bg-white rounded-xl border border-slate-200 p-4 mb-5 shadow-sm"
          data-testid="required-work-header"
        >
          <div className="flex items-baseline gap-3 flex-wrap">
            {data?.reg && (
              <span
                className="font-mono font-bold text-lg tracking-wide px-2 py-0.5 rounded bg-slate-900 text-white"
                data-testid="required-work-reg"
              >
                {data.reg}
              </span>
            )}
            {data?.gh_job_ref && (
              <span
                className="text-xs uppercase tracking-wider text-slate-500 font-semibold"
                data-testid="required-work-gh-ref"
              >
                {data.gh_job_ref}
              </span>
            )}
          </div>
          {(vehicleLine || customerLine) && (
            <p className="mt-1 text-sm text-slate-600">
              {[vehicleLine, customerLine].filter(Boolean).join(" · ")}
            </p>
          )}
        </section>

        <h1
          className="text-2xl font-bold text-slate-900 mb-3"
          data-testid="required-work-title"
        >
          Required Work
        </h1>
        <p className="text-sm text-slate-500 mb-4">
          Choose the task to start now.
        </p>

        {/* Outstanding — big tap targets */}
        <ul className="space-y-3" data-testid="required-work-outstanding-list">
          {outstanding.map((w) => {
            const Icon = ICON_MAP[w.key] || SearchIcon;
            const tint = WORKFLOW_TINT[w.key] || WORKFLOW_TINT.vehicle_inspection;
            return (
              <li key={w.key}>
                <button
                  type="button"
                  onClick={() => navigate(w.start_url)}
                  className={`w-full flex items-center gap-4 rounded-xl border-2 ${tint.ring} ${tint.tile} px-5 py-6 text-left transition-colors shadow-sm active:scale-[0.99]`}
                  data-testid={`required-work-start-${w.key}`}
                >
                  <div className={`w-14 h-14 rounded-full bg-white flex items-center justify-center border ${tint.ring}`}>
                    <Icon className={`w-7 h-7 ${tint.icon}`} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className={`text-lg font-bold ${tint.text}`}>{w.label}</div>
                    <div className={`text-sm font-semibold ${tint.text} opacity-80 mt-0.5`}>
                      {w.start_label}
                    </div>
                  </div>
                  <ArrowRight className={`w-6 h-6 ${tint.icon}`} />
                </button>
              </li>
            );
          })}
        </ul>

        {/* Completed — visible but not competing */}
        {completed.length > 0 && (
          <section className="mt-8" data-testid="required-work-completed-list">
            <div className="text-[10px] uppercase tracking-[0.22em] text-slate-400 font-bold mb-2">
              Already complete
            </div>
            <ul className="space-y-2">
              {completed.map((w) => {
                const Icon = ICON_MAP[w.key] || SearchIcon;
                return (
                  <li
                    key={w.key}
                    className="flex items-center gap-3 rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3"
                    data-testid={`required-work-done-${w.key}`}
                  >
                    <CheckCircle2 className="w-5 h-5 text-emerald-600 shrink-0" />
                    <Icon className="w-4 h-4 text-emerald-700 shrink-0" />
                    <span className="text-sm font-semibold text-emerald-900">
                      {w.label}
                    </span>
                    <span className="ml-auto text-[11px] uppercase tracking-wider text-emerald-700 font-bold">
                      Done
                    </span>
                  </li>
                );
              })}
            </ul>
          </section>
        )}
      </div>
    </div>
  );
}
